'use client';

import { useEffect } from 'react';

import { type PluginName, usePluginAuthState } from '~/hooks/api/usePluginAuthState';
import { getPersistedPluginParams } from '~/utils/pluginParams';

export type { PluginName };

export type UseOAuthRedirectParams = {
  apiEndpoint: string;
  callbackRoute: string;
  id: string;
  pluginName: PluginName;
};

export const useOAuthRedirect = ({ apiEndpoint, callbackRoute, id, pluginName }: UseOAuthRedirectParams) => {
  const { data, isLoading } = usePluginAuthState({
    apiEndpoint,
    id,
    pluginName,
  });

  useEffect(() => {
    if (data?.code && data?.state) {
      /**
       * We need to redirect using `window.location.href` because
       * the useRouter hook will not trigger `amplify-js` URL listener
       * to handle the OAuth flow.
       *
       * This is a hard navigation — the browser re-checks CSP frame-ancestors.
       * Append workspaceId and hostOrigin from sessionStorage so the middleware
       * can validate the embedding origin for custom-domain studios.
       */
      const url = new URL(callbackRoute, window.location.origin);
      url.searchParams.set('code', data.code);
      url.searchParams.set('state', data.state);
      url.searchParams.set('in_plugin', 'true');

      const { hostOrigin, projectId, workspaceId } = getPersistedPluginParams();

      if (hostOrigin) {
        url.searchParams.set('hostOrigin', hostOrigin);
      }
      if (projectId) {
        url.searchParams.set('projectId', projectId);
      }
      if (workspaceId) {
        url.searchParams.set('workspaceId', workspaceId);
      }

      window.location.href = url.toString();
    }
  }, [data?.code, data?.state, callbackRoute]);

  return { isOAuthRedirectLoading: isLoading };
};
